/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LoadingOutlined } from '@ant-design/icons';
import PlusSquare from '@untitled-ui/icons-react/build/esm/PlusSquare';

import { Input, Spin } from 'antd';
import { setDrawerCloseDisabled } from 'api/client-side-slices/DesignSlice';
import { setAbnSearchIdentifier, setExistingCompany } from 'api/client-side-slices/GroupsSlice';
import { usePrimeSearchMutation } from 'api/EntitiesApiSlice';
import { useLazyCompaniesDetailListQuery } from 'api/RegistryApiSlice';
import { useAppDispatch, useAppSelector } from 'app-redux/hooks';
import { BaseButton, SearchInput } from 'components';
import { GetEntityModel } from 'solstice-types';
import { useDebounce } from 'use-debounce';
import { UserModel } from 'utils';
import { SelectedCompany } from '../components/SelectedCompany';

interface EntitySearchStepProps {
  setStep: (step: number) => void;
  onSelectPrime: (cb?: () => void) => void;
  onSelectKYC: (cb?: () => void) => void;
}

export const EntitySearchStep = ({
  setStep,
  onSelectPrime,
  onSelectKYC,
}: EntitySearchStepProps) => {
  const { existingCompany } = useAppSelector((state) => state.groups);
  const entityLabel = 'company';
  const addNewEntityLabel = 'Add New Company';

  const [kycSearch, { data: kycSearchData, isLoading: isKycSearchLoading }] =
    usePrimeSearchMutation();
  const [primeSearch, { data: primeSearchData, isLoading: isPrimeSearchLoading, isFetching }] =
    useLazyCompaniesDetailListQuery();

  const [isExistingVisible, setIsExistingVisible] = useState<boolean>(false);

  const [kycInputValue, setkycInputValue] = useState<string>('');
  const [primeInputValue, setPrimeInputValue] = useState<string>('');

  const [debouncedKYCQuery] = useDebounce(kycInputValue, 400);
  const [debouncedPrimeQuery] = useDebounce(primeInputValue, 400);

  const [hasError, setHasError] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { control, reset } = useFormContext();

  const kycSearchOptions =
    hasError || (kycInputValue && kycSearchData?.length === 0)
      ? [
          {
            value: 'no-result',
            label: <div className="text-gray-500 font-medium">No results found.</div>,
          },
        ]
      : kycSearchData?.map((user: UserModel) => ({
          value: user.id,
          label: <SelectedCompany user={user} searchQuery={kycInputValue} areOptionsVisible />,
        }));

  const primeSearchOptions =
    hasError || (primeInputValue && primeInputValue?.length === 0)
      ? [
          {
            value: 'no-result',
            label: <div className="text-gray-500 font-medium">No results found.</div>,
          },
        ]
      : [...(primeSearchData?.data ?? [])]
          ?.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
          .map((company) => ({
            value: company.regNo,
            label: company.companyName,
          }));

  const handleSelectKYC = (value: string) => {
    if (value === 'no-result' || value === 'loading') return;

    const selectedItem = kycSearchData?.find((item) => item.id === value);
    if (selectedItem) {
      const { acn, abn } = selectedItem;

      onSelectKYC();
      setIsExistingVisible(true);
      setkycInputValue('');
      dispatch(setExistingCompany(selectedItem));
      dispatch(
        setAbnSearchIdentifier({
          entityIdentifier: (acn ?? abn) || '',
          identifierType: acn ? 'acn' : 'abn',
          entityId: selectedItem.id ?? '',
        })
      );
      dispatch(setDrawerCloseDisabled(false));
    }
  };

  const handleSelectPrime = (value: string) => {
    if (value === 'no-result' || value === 'loading') return;

    const selectedItem = primeSearchData?.data?.find((company) => company.regNo === value);
    if (selectedItem) {
      const { regNo, abn, status, companyName, regPlace } = selectedItem;
      const acn = regNo?.replaceAll(' ', '');

      const entity: GetEntityModel = {
        abn,
        acn,
        status,
        registeredName: companyName,
        location: regPlace,
      };

      onSelectPrime();
      setIsExistingVisible(true);
      setPrimeInputValue('');
      dispatch(setExistingCompany(entity));
      dispatch(
        setAbnSearchIdentifier({
          entityIdentifier: (acn ?? abn) || '',
          identifierType: acn ? 'acn' : 'abn',
          entityId: regNo ?? '',
        })
      );
      dispatch(setDrawerCloseDisabled(false));
    }
  };

  const handleKYCSearch = (value: string) => {
    setkycInputValue(value);
    dispatch(setExistingCompany(null));

    if (!value) {
      dispatch(setExistingCompany(null));
      setIsExistingVisible(false);
    }
  };

  const handlePrimeSearch = (value: string) => {
    setPrimeInputValue(value);
    dispatch(setExistingCompany(null));

    if (!value) {
      dispatch(setExistingCompany(null));
      setIsExistingVisible(false);
    }
  };

  const handleDisplayEntity = () => {
    dispatch(setDrawerCloseDisabled(true));
    dispatch(setExistingCompany(null));
    setkycInputValue('');
    reset();
    setIsExistingVisible(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedKYCQuery) {
        setHasError(false);
        try {
          await kycSearch({ searchTerm: debouncedKYCQuery, entityType: 'Company' }).unwrap();
        } catch (error: any) {
          if (error.status === 404) {
            setHasError(true);
          }
        }
      }
    };

    fetchData();
  }, [debouncedKYCQuery, kycSearch]);

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedPrimeQuery) {
        setHasError(false);
        try {
          await primeSearch(debouncedPrimeQuery).unwrap();
        } catch (error: any) {
          if (error.status === 404) {
            setHasError(true);
          }
        }
      }
    };

    fetchData();
  }, [debouncedPrimeQuery, primeSearch]);

  return (
    <div className="flex flex-col justify-between overflow-auto h-[calc(100vh-130px)]">
      <div className="flex justify-start items-start flex-col ml-1.5 mr-1.5 gap-[20px]">
        <span className="font-bold text-2xl text-gray-800">Start New Verification</span>
        <span className="text-gray-600">
          You can search for an existing {entityLabel} or add a new one.
        </span>

        <Controller
          name="primeEntityName"
          control={control}
          render={({ field }) => (
            <SearchInput
              {...field}
              inputClassName="h-10 w-full"
              onSearch={handleKYCSearch}
              options={kycSearchOptions}
              onSelect={handleSelectKYC}
              value={kycInputValue}
              placeholder="Search an existing company from previous KYC verifications."
              autoFocus
              suffixIcon={
                isKycSearchLoading ? (
                  <Spin className="mt-2" size="default" indicator={<LoadingOutlined spin />} />
                ) : (
                  ''
                )
              }
            />
          )}
        />

        <Controller
          name="complianceEntityName"
          control={control}
          render={({ field }) => (
            <SearchInput
              {...field}
              inputClassName="h-10 w-full"
              onSearch={handlePrimeSearch}
              options={primeSearchOptions}
              onSelect={handleSelectPrime}
              value={primeInputValue}
              placeholder="Load details from an existing company from Prime Compliance."
              autoFocus
              suffixIcon={
                isPrimeSearchLoading || isFetching ? (
                  <Spin className="mt-2" size="default" indicator={<LoadingOutlined spin />} />
                ) : (
                  ''
                )
              }
            />
          )}
        />

        <Controller
          name="searchType"
          control={control}
          render={({ field }) => <Input {...field} type="hidden" />}
        />

        {existingCompany && (
          <SelectedCompany
            user={existingCompany}
            searchQuery={kycInputValue}
            isExistingVisible={isExistingVisible}
            handleDisplayCompany={handleDisplayEntity}
          />
        )}

        <BaseButton className="link-button" onClick={() => setStep(1)}>
          <PlusSquare width={15} height={15} />
          {addNewEntityLabel}
        </BaseButton>
      </div>
    </div>
  );
};
